import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DataTableComponentFrontPagination from "../../common/DataTableComponentFrontPagination";
import { useFirstAidRestockList } from "../../../query/hooks/QueryHooksFirstAidBox";
import { CONDITION_LABELS, OHS_BASE_PATH } from "./ohsConstants";
import { MutedText } from "./Ohs.styles";
import { getItemStatuses } from "./ohsUtils";

const EXPORT_HEADER = ["Campus", "Item", "Unit", "Boxes Needing It", "Units To Order", "Boxes"];
const EMPTY_FILTER = { arr: [], checkObj: {} };

/**
 * Restock list - every low, damaged or expired item in a box's current stock, one line per campus and
 * item, since stock is ordered for delivery to a specific campus. Filter to a campus and export it as
 * that campus's order sheet.
 */
function RestockList() {
    const { data: restockList, isLoading } = useFirstAidRestockList();
    const [search, setSearch] = useState("");
    const [campusFilter, setCampusFilter] = useState(EMPTY_FILTER);

    const campusOptions = useMemo(() => {
        const campusNamesById = new Map(restockList.map((restockLine) => [String(restockLine.campus_id), restockLine.campus_name || "No campus"]));
        return [...campusNamesById.entries()].map(([campusId, campusName]) => ({ value: campusId, label: campusName }));
    }, [restockList]);

    const filteredRestockList = useMemo(() => {
        const searchText = search.trim().toLowerCase();
        return restockList
            .map((restockLine) => ({ ...restockLine, rowKey: `${restockLine.campus_id}-${restockLine.box_item_id}` }))
            .filter((restockLine) => !campusFilter.arr.length || campusFilter.arr.includes(String(restockLine.campus_id)))
            .filter((restockLine) => {
                return !searchText
                    || restockLine.item_name.toLowerCase().includes(searchText)
                    || (restockLine.campus_name || "").toLowerCase().includes(searchText)
                    || restockLine.boxes.some((restockBox) => restockBox.box_code.toLowerCase().includes(searchText));
            });
    }, [restockList, search, campusFilter]);

    const columns = useMemo(
        () => [
            {
                name: "Campus",
                selector: "campus_name",
                sortable: true,
                minWidth: "140px",
                cell: (row) => row.campus_name || "-",
            },
            {
                name: "Item",
                selector: "item_name",
                sortable: true,
                grow: 2,
                minWidth: "220px",
                cell: (row) => (
                    <div>
                        <div>{row.item_name}</div>
                        <MutedText>Box holds {row.expected_quantity} {row.unit}</MutedText>
                    </div>
                ),
            },
            {
                name: "Boxes Needing It",
                selector: "boxCount",
                sortable: true,
                minWidth: "140px",
            },
            {
                name: "Units To Order",
                selector: "totalUnitsToOrder",
                sortable: true,
                minWidth: "120px",
            },
            {
                name: "Boxes",
                grow: 3,
                minWidth: "280px",
                cell: (row) => (
                    <div>
                        {row.boxes.map((restockBox) => (
                            <div key={restockBox.box_id}>
                                <Link to={`${OHS_BASE_PATH}/boxes/open/${restockBox.box_id}`}>{restockBox.box_code}</Link>
                                {" "}
                                <MutedText>
                                    {getItemStatuses(restockBox).map((itemStatus) => CONDITION_LABELS[itemStatus]).join(", ")}, {restockBox.quantity} left, {restockBox.unitsToOrder} to order · {restockBox.building_name}
                                </MutedText>
                            </div>
                        ))}
                    </div>
                ),
            },
        ],
        []
    );

    /**
     * Rows for the CSV/XLSX/PDF export - doubles as an order sheet per campus.
     * @returns {Object} { header, data, pdfData }
     */
    const exportData = () => {
        const exportRows = filteredRestockList.map((restockLine) => [
            restockLine.campus_name,
            restockLine.item_name,
            restockLine.unit,
            restockLine.boxCount,
            restockLine.totalUnitsToOrder,
            restockLine.boxes.map((restockBox) => restockBox.box_code).join(", "),
        ]);

        return {
            header: EXPORT_HEADER,
            data: exportRows.map((exportRow) => Object.fromEntries(EXPORT_HEADER.map((columnName, columnIndex) => [columnName, exportRow[columnIndex]]))),
            pdfData: exportRows,
        };
    };

    return (
        <DataTableComponentFrontPagination
            data={filteredRestockList}
            columns={columns}
            loading={isLoading}
            keyField="rowKey"
            search={search}
            setSearch={setSearch}
            exportData={exportData}
            exportFileName="First_Aid_Restock_List"
            defaultSort={{ defaultSortColumn: "campus_name", defaultSortAsc: true }}
            filters={[
                {
                    filterName: "Campus",
                    optionArr: campusOptions,
                    state: campusFilter,
                    setState: setCampusFilter,
                    uniqueId: "ohsRestockCampus",
                },
            ]}
        />
    );
}

export default RestockList;
